import React, { JSX } from 'react';
import { useTranslation } from 'react-i18next';
import CommentOutlined from '@mui/icons-material/CommentOutlined';
import Constants from 'style/constants';
import OrganisationBreadCrumb from 'shared/components/bread-crumb/organisation-bread-crumb/organisation-bread-crumb';
import LoadingIndicator from 'shared/components/loading-indicator/loading-indicator';
import MonitoringIcon from 'shared/components/monitoring/monitoring-icon';
import { MonitoringType } from 'shared/components/monitoring/monitoring-type';
import PageHeader from 'shared/components/page-header/page-header';
import PopUp from 'shared/components/pop-up/pop-up';
import { ColumnDefinition } from 'shared/components/table/columnDefinition';
import { TableOverflowMenuItem } from 'shared/components/table/hooks';
import Table from 'shared/components/table/table';
import RowItem from 'shared/components/table/types/row-item';
import { NestedKeyof } from 'shared/components/table/utils/nested-keyof-utils';
import Permission from 'shared/features/auth/authorization/enums/permission';
import usePermissionAuthorization from 'shared/features/auth/authorization/hooks/permission-authorization';
import { findUpstreamParentBranch } from 'shared/features/organisation/models/organisation-tree-node';
import { useOrganisationUnitTreeContext } from 'shared/features/organisation/providers/organisation-unit-tree-provider';

import IntegrationsNavigation from 'features/integrations/views/integrations-navigation';
import { useSystemsContextProvider } from 'features/systems/context/systems-provider';
import { RemoveSystemValidationFailedReason } from 'features/systems/domain/models/remove-system-validation-failed-reason';
import { System } from 'features/systems/domain/models/system';
import { SystemStatus } from 'features/systems/domain/models/system-status';
import SystemFilters from 'features/systems/views/components/system-filters';
import SystemDetailsPopup from 'features/systems/views/details/system-details-popup';
import styles from 'features/systems/views/systems.module.scss';

const Systems = (): React.JSX.Element => {
  const { t } = useTranslation('systems');
  const { hasPermission } = usePermissionAuthorization();
  const { organisationUnitTree } = useOrganisationUnitTreeContext();

  const {
    systemsResponse,
    systemsIsLoading,
    systemsIsSuccess,

    systemsFilterHook,
    systemsDetailsHook,

    openDetailsInViewingMode,
    openDetailsInEditingMode,
  } = useSystemsContextProvider();

  const {
    selectedSystem,
    mutationIsLoading,
    isDetailsOpen,
    isDeleteConfirmationPopupOpen,
    isDeleteErrorPopupOpen,
    systemValidationFailedReason,
    deleteSystem,
    closeDeleteConfirmationPopup,
    deleteConfirmationAction,
    closeDeleteErrorPopup,
  } = systemsDetailsHook;

  const { systems, total } = systemsResponse ?? { systems: [], total: 0 };
  const showSystemsTable = !systemsIsLoading && systemsIsSuccess;

  const organisationBreadCrumb = (system: System): React.ReactElement => {
    if (!system.organisationUnitId || !organisationUnitTree) return <></>;

    const upstreamParentBranch = findUpstreamParentBranch(organisationUnitTree, system.organisationUnitId, []);

    if (!upstreamParentBranch) return <></>;

    return (
      <OrganisationBreadCrumb
        rootNodeId={system.organisationUnitId}
        upstreamParentBranch={upstreamParentBranch}
        maxVisibleBreadCrumbs={2}
      />
    );
  };

  const columns: ColumnDefinition<System, NestedKeyof<System>>[] = [
    { key: 'name', label: t('table.column.name') },
    {
      key: 'type',
      label: t('table.column.type'),
      renderCustomContentProvider: ({ type }) => <>{t(`details.information.type.${type}`)}</>,
      disableSort: true,
    },
    { key: 'hostName', label: t('table.column.hostName') },
    {
      key: 'organisationUnitId',
      label: t('table.column.organisation'),
      renderCustomContentProvider: organisationBreadCrumb,
      disableSort: true,
      tableCellProps: { width: '400px' },
    },
    {
      key: 'note',
      label: '',
      renderCustomContentProvider: (system) => <div className={styles.iconRow}>{getIcons(system)}</div>,
      disableSort: true,
      tableCellProps: {
        align: 'right',
        width: '24px',
      },
    },
  ];

  const getIcons = (system: System): JSX.Element[] => {
    const result: JSX.Element[] = [];

    result.push(
      <CommentOutlined
        data-testid="noteIcon"
        key={'note-icon'}
        sx={{
          width: 16,
          height: 16,
          color: Constants.Colors.onSurfaceVariant,
          visibility: system.note ? 'visible' : 'hidden',
        }}
      />,
    );

    if (system.status === SystemStatus.offline) {
      result.push(
        <MonitoringIcon
          key={`monitoring-icon-${MonitoringType.SystemOffline}`}
          type={MonitoringType.SystemOffline}
          withTooltip={true}
          visible={true}
          size={16}
        />,
      );
    } else if (system.status === SystemStatus.malfunction) {
      result.push(
        <MonitoringIcon
          key={`monitoring-icon-${MonitoringType.SystemMalfunction}`}
          type={MonitoringType.SystemMalfunction}
          withTooltip={true}
          visible={true}
          size={16}
        />,
      );
    } else if (system.status === SystemStatus.warning) {
      result.push(
        <MonitoringIcon
          key={`monitoring-icon-${MonitoringType.SystemWarning}`}
          type={MonitoringType.SystemWarning}
          withTooltip={true}
          visible={true}
          size={16}
        />,
      );
    } else if (system.status === SystemStatus.online) {
      result.push(
        <MonitoringIcon
          key={`monitoring-icon-${MonitoringType.SystemOnline}`}
          type={MonitoringType.SystemOnline}
          withTooltip={true}
          visible={true}
          size={16}
        />,
      );
    }

    return result;
  };

  const getOverflowMenuItems = (): Array<TableOverflowMenuItem<System>> | undefined => {
    const menuItems: Array<TableOverflowMenuItem<System>> = [
      {
        label: t('table.overflowMenu.view'),
        action: (system) => openDetailsInViewingMode(system.id),
      },
    ];

    if (hasPermission(Permission.UpdateSystems)) {
      menuItems.push({
        label: t('table.overflowMenu.edit'),
        action: (system) => openDetailsInEditingMode(system.id),
      });
    }
    if (hasPermission(Permission.DeleteSystems)) {
      menuItems.push({ label: 'divider' });
      menuItems.push({
        label: t('table.overflowMenu.delete'),
        action: (system) => deleteSystem(system),
      });
    }

    return menuItems;
  };

  return (
    <div className={styles.container}>
      <PageHeader
        title={t('pageTitle')}
        navigationComponent={<IntegrationsNavigation />}
        filterComponent={
          systemsFilterHook.monitorFiltersIsSuccess ? (
            <div className={styles.filterComponentContainer}>
              <SystemFilters />
            </div>
          ) : (
            <></>
          )
        }
      />
      {systemsIsLoading && <LoadingIndicator />}
      {showSystemsTable && (
        <Table
          data={systems.map<RowItem<System>>((system) => ({ data: system }))}
          columns={columns}
          onItemClick={(system: System) => openDetailsInViewingMode(system.id)}
          overflowMenuOptions={getOverflowMenuItems()}
          rowIdentifier={(item) => item.id ?? ''}
          initialOrderBy={systemsFilterHook.table.sortFromSessionStorage?.property}
          initialOrderDirection={systemsFilterHook.table.sortFromSessionStorage?.isAscending ? 'asc' : 'desc'}
          useBuiltInSorter
          pagination={{
            count: total,
            page: systemsFilterHook.table.currentPage,
            rowsPerPage: systemsFilterHook.table.currentRowsPerPage,
            onPageChanged: systemsFilterHook.table.handleOnPageChanged,
            onRowsPerPageChanged: systemsFilterHook.table.handleOnRowsPerPageChanged,
          }}
          onSortChanged={systemsFilterHook.table.handleOnSortChanged}
        />
      )}
      {isDetailsOpen && <SystemDetailsPopup />}
      <PopUp
        isOpen={isDeleteConfirmationPopupOpen}
        title={t('details.deleteSystemConfirmation.title', {
          systemName: selectedSystem?.name,
        })}
        body={t('details.deleteSystemConfirmation.body')}
        primaryButtonText={t('details.deleteSystemConfirmation.confirm')}
        secondaryButtonText={t('details.deleteSystemConfirmation.cancel')}
        handleOnClose={closeDeleteConfirmationPopup}
        secondaryButtonAction={closeDeleteConfirmationPopup}
        primaryButtonAction={() => deleteConfirmationAction?.()}
        isLoadingPrimaryAction={mutationIsLoading}
      />
      <PopUp
        isOpen={isDeleteErrorPopupOpen}
        title={t('details.deleteSystemError.title', {
          systemName: selectedSystem?.name,
        })}
        body={t(
          systemValidationFailedReason === RemoveSystemValidationFailedReason.IntercomMappings
            ? 'details.deleteSystemError.intercomMappingsBody'
            : 'details.deleteSystemError.linkedDevicesBody',
          {
            systemName: selectedSystem?.name,
          },
        )}
        primaryButtonText={t('details.deleteSystemError.confirm')}
        handleOnClose={closeDeleteErrorPopup}
        primaryButtonAction={closeDeleteErrorPopup}
      />
    </div>
  );
};

export default Systems;
